import { cn } from '#app/utils/misc.tsx'

export type TimelineItem = {
	marker: React.ReactNode
	title: React.ReactNode
	accessory?: React.ReactNode
	body?: React.ReactNode
}

type Props = {
	items: TimelineItem[]
	className?: string
}

/**
 * Vertical ladder/timeline. A single connecting line runs through the
 * geometric center of the marker column; markers are centered in a fixed
 * 48px square and given a background fill so the line appears to pass
 * behind them.
 *
 * Use for: process steps, ranked levels, roadmap phases.
 */
export function Timeline({ items, className }: Props) {
	return (
		<ol className={cn('relative', className)}>
			{/* Connecting line through marker centers (24px from the left edge). */}
			<span
				aria-hidden
				className="bg-border absolute top-6 bottom-6 left-6 w-px"
			/>
			{items.map((item, i) => (
				<li
					key={i}
					className="relative flex items-start gap-5 pb-10 last:pb-0 md:gap-6"
				>
					{/* Marker: fixed 48x48, centered. Has bg so the line "passes behind". */}
					<div className="bg-background relative z-10 flex size-12 shrink-0 items-center justify-center">
						<span className="text-primary text-3xl leading-none font-black tracking-tight md:text-4xl">
							{item.marker}
						</span>
					</div>

					<div className="flex-1 pt-1">
						<div className="flex flex-wrap items-center gap-3">
							<h3 className="text-foreground text-lg font-bold tracking-tight md:text-xl">
								{item.title}
							</h3>
							{item.accessory}
						</div>
						{item.body ? (
							<div className="text-foreground/85 mt-2 text-sm leading-relaxed md:text-base">
								{item.body}
							</div>
						) : null}
					</div>
				</li>
			))}
		</ol>
	)
}
